import { api } from '@/services/api'


import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

interface FeePayment {
  id: string
  amountPaid: string
  paymentDate: string
  receiptNumber: string
  paymentMode: string
  paymentStatus: string
  isActive: boolean
  student: {
    rollNumber: string
    firstName: string
    lastName: string
  }
}

interface FeeState {
  payments: FeePayment[]
  currentPayment: FeePayment | null
  isLoading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

const initialState: FeeState = {
  payments: [],
  currentPayment: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
}

export const fetchFeePayments = createAsyncThunk(
  'fee/fetchFeePayments',
  async (params: { page?: number; limit?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await api.get('/fees/payments', { params })
      return response.data // Backend returns { success, message, data, meta }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payments')
    }
  }
)

const feeSlice = createSlice({
  name: 'fee',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentPayment: (state, action) => {
      state.currentPayment = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeePayments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchFeePayments.fulfilled, (state, action) => {
        state.isLoading = false
        state.payments = action.payload.data
        state.pagination = {
          page: action.payload.meta.page,
          limit: action.payload.meta.limit,
          total: action.payload.meta.total,
          totalPages: action.payload.meta.totalPages,
        }
      })
      .addCase(fetchFeePayments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, setCurrentPayment } = feeSlice.actions
export default feeSlice.reducer
